<script setup lang="ts">
	import { computed } from 'vue'

	import StyledAlertList from '@/components/StyledAlertList.vue'

	import { PolicyInformation } from '@/interfaces/policyInformation'

	interface Props {
		policyInformation?: PolicyInformation
		context?: 'request' | 'reservation'
	}

	const props = withDefaults(defineProps<Props>(), {
		context: 'request',
	})

	const texts = computed(() => {
		const { errors = [], warnings = [] } = props.policyInformation ?? {}

		return {
			errorTexts: {},
			warningTexts: {
				intro:
					errors.length ?
						'Además, esta solicitud no cumple con las condiciones de algunas'
					:	'Esta solicitud no cumple con las condiciones de algunas políticas. ',
				continue: errors.length ? 'podrá continuar' : 'Podrá continuar',
				end:
					props.context === 'request' ?
						'realizar alguna reserva'
					:	'concluir con esta reserva',
			},
		}
	})
</script>

<template>
	<div
		class="flex flex-col gap-4 max-w-4xl my-8 mx-auto"
		v-show="
			policyInformation?.errors?.length || policyInformation?.warnings?.length
		"
	>
		<StyledAlertList
			v-if="policyInformation?.errors?.length"
			sub-title="Políticas infringidas:"
			type="Error"
			:alerts="policyInformation.errors.map(({ message }) => message)"
			:show-limit="2"
			:automatic-scroll="false"
		>
			<template #title>
				<p class="text-sm font-semibold">
					Esta solicitud no cumple con las condiciones de algunas
					<span class="important">políticas</span> establecidas;
					<span class="important">no es posible continuar</span> con la reserva
					hasta que se realicen los ajustes necesarios.
				</p>
			</template>
		</StyledAlertList>

		<StyledAlertList
			v-if="policyInformation?.warnings?.length"
			sub-title="Politicas infringidas:"
			type="Warning"
			:alerts="policyInformation.warnings.map(({ message }) => message)"
			:show-limit="2"
			:automatic-scroll="false"
		>
			<template #title>
				<p class="text-sm font-semibold">
					{{ texts.warningTexts.intro }}

					<template v-if="policyInformation.errors?.length">
						<VDropdown
							class="inline"
							placement="top"
						>
							<span class="important cursor-pointer">políticas flexibles</span>

							<template #popper>
								<p class="p-4 max-w-[18rem] text-sm">
									Una política flexible no restringe una solicitud, pero muestra
									advertencias y notifica a su supervisor en caso de realizar
									alguna reservación bajo estas condiciones.
								</p>
							</template> </VDropdown
						>; al solucionar las políticas anteriores
					</template>

					<span class="important">{{ texts.warningTexts.continue }}</span> aún
					con estas advertencias, pero se le notificará a su superior en caso de
					{{ texts.warningTexts.end }}.
				</p>
			</template>
		</StyledAlertList>
	</div>
</template>

<style scoped>
	.important {
		@apply text-sm font-bold underline underline-offset-4;
	}
</style>
